import {type SagaIterator} from 'redux-saga';
import type {AxiosPromise} from 'axios';
import {FULFILLED} from 'redux-promise-middleware';
import {sendEvent} from '@optimaxdev/analytics/mobile';

import {APP, ANALYTICS, NEWSLETTER, CUSTOMER, LOCATION_CHANGED} from 'constants/actions';
import type {ActionType} from 'constants/flow/flowTypes';

import {doTransform} from 'libs/transformers';
import {isCustomerSubscribed} from 'selectors/dynamicYield/dynamicYield';
import {getUrlParams} from 'libs/url';
import {removeDYFromDocument} from 'libs/domManipulations/domManipulations';
import {takeLatest, takeEvery, select, call, take} from 'redux-saga/effects';
import {fetchSelf, getCustomer, getToken} from 'reducers/oAuth/oAuth';
import {getProductsECCData} from 'sagas/EEC/EECProductEvents/EECProductEvents';

/**
 * Send event that a user subscribed to the newsletter
 * @param {ActionType<AxiosPromise>} action - Action
 */
export function* newsletterSubscription(action: ActionType<AxiosPromise>): SagaIterator {
    const hashedEmail = doTransform(action.meta.email, 'sha256');

    yield call(sendEvent, 'event', {
        name: 'Newsletter Subscription',
        properties: {
            dyType: 'newsletter-subscription-v1',
            hashedEmail,
        },
    });
}

/**
 * Send event that a user logged in
 */
export function* logIn(): SagaIterator {
    const oAuth = yield select(getCustomer);
    yield call(sendEvent, 'event', {
        name: 'Login',
        properties: {
            dyType: 'login-v1',
            hashedEmail: oAuth.sha256,
        },
    });
}

/**
 * Send event that a user signed up
 */
export function* signUp(): SagaIterator {
    const oAuth = yield select(getCustomer);
    yield call(sendEvent, 'event', {
        name: 'Signup',
        properties: {
            dyType: 'signup-v1',
            hashedEmail: oAuth.sha256,
        },
    });
}

/**
 * Listen when the customer data was received
 * @param {{payload: {type}}} type - Type of action
 */
export function* getCustomerListener({payload: {type}}): SagaIterator {
    yield takeLatest(`${fetchSelf}_${FULFILLED}`, type === 'login' ? logIn : signUp);
}

/**
 * Send DY event on landing on page within banner or other marketing stuff
 */
export function* marketingRedirectPageDY(): SagaIterator {
    const queryParams = yield call(getUrlParams);
    const {utm_source = null, utm_medium = null, utm_campaign = null, affid = null} = queryParams; // eslint-disable-line camelcase

    if (!utm_source && !utm_medium && !utm_campaign && !affid) return; // eslint-disable-line camelcase

    yield take(`${CUSTOMER.GET_CUSTOMER_DATA}_FULFILLED`);
    const isSubscribed = yield select(isCustomerSubscribed);
    yield call(sendEvent, 'event', {
        name: 'GUSA_landing_page',
        properties: {
            utm_source,
            utm_medium,
            utm_campaign,
            affid,
            is_user_subscribed: Number(isSubscribed),
        },
    });
}

/**
 * Clear dynamic yield nodes from document.
 */
export function* clearDYNodes(): SagaIterator {
    yield call(removeDYFromDocument);
}

/**
 * Send DY event when changes attribute for product.
 * @param {ActionType<void, {type: string, value: string}>} action - Action.
 */
export function* changeAttribute(
    action: ActionType<void, {type: string, value: string}>,
): SagaIterator {
    const {type, value} = action.meta;

    yield call(sendEvent, 'event', {
        name: 'Change Attribute',
        properties: {
            dyType: 'change-attr-v1',
            attributeType: type,
            attributeValue: value,
        },
    });
    yield call(getProductsECCData, type);
}

/**
 * Send DY event when user uses search.
 * @param {ActionType<void, {value: string}>} action - Action.
 */
export function* keywordSearch(action: ActionType<void, {value: string}>): SagaIterator {
    yield call(sendEvent, 'event', {
        name: 'Keyword Search',
        properties: {
            dyType: 'keyword-search-v1',
            keywords: action.meta.value,
        },
    });
}

/**
 * Support saga function for DY common event
 */
export default function* dynamicYieldSagaCustomer(): SagaIterator {
    yield takeEvery(`${NEWSLETTER.SUBSCRIBE}_${FULFILLED}`, newsletterSubscription);
    yield takeEvery(getToken, getCustomerListener); // todo Mary return back when registration returns token
    yield takeLatest(APP.SAVE_LOCATION, marketingRedirectPageDY);
    yield takeEvery(LOCATION_CHANGED, clearDYNodes);
    yield takeEvery(ANALYTICS.DY_CHANGE_ATTRIBUTE, changeAttribute);
    yield takeEvery(ANALYTICS.KEYWORD_SEARCH, keywordSearch);
}
